
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  IoBookOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoBusinessOutline,
  IoCogOutline,
  IoGameControllerOutline,
  IoGiftOutline,
  IoHeadsetOutline,
  IoHomeOutline,
  IoLanguageOutline,
  IoLockClosedOutline,
  IoLogOutOutline,
  IoNewspaperOutline,
  IoPeopleOutline,
  IoStatsChartOutline,
  IoTrophyOutline,
  IoTimeOutline,
} from "react-icons/io5";
import { GiBinoculars } from "react-icons/gi";
import { useAuth } from "../services/AuthContext";
import DirectMessages from "../components/DirectMessages";
import {
  normalizeLanguage,
  setStoredLanguage,
} from "../services/language";
import { detectBrowserLanguage, setLanguage as setI18nLanguage, t, t as i18nT } from "../i18n";
import {
  COMMON_BLOCK_PRODUCTS,
  SPECIAL_BLOCK_PRODUCTS,
} from "../constants/skinStoreCatalog";
const FRIEND_ALERT_SEEN_KEY = "xima_friends_alert_seen_ts";
const GLOBAL_CHAT_SESSION_KEY = "xima_global_chat_messages_session_v1";
const NEWS_ALERT_SEEN_KEY = "xima_news_alert_seen_ts_v1";
const SUPPORT_ALERT_SEEN_KEY = "xima_support_alert_seen_ts_v1";
const LEARN_ALERT_SEEN_VERSION_KEY = "xima_learn_alert_seen_version_v1";
const STORE_ALERT_SEEN_COUNT_KEY = "xima_store_alert_seen_count_v1";
const LEARN_CONTENT_VERSION = "2026-05-04.learn.v1";
const STORE_PRODUCTS_TOTAL_COUNT =
  (Array.isArray(SPECIAL_BLOCK_PRODUCTS) ? SPECIAL_BLOCK_PRODUCTS.length : 0) +
  (Array.isArray(COMMON_BLOCK_PRODUCTS) ? COMMON_BLOCK_PRODUCTS.length : 0);
const SIDEBAR_BADGE_KEYS = new Set([
  "news",
  "learn",
  "store",
  "friends",
  "donate",
  "superusers",
]);
const NEWS_BOARD_EVENT_NAME = "NEWS_BOARD_MESSAGE";
const NEWS_BOARD_HISTORY_EVENT_NAME = "NEWS_BOARD_HISTORY";
const NEWS_BOARD_HISTORY_REQUEST_EVENT_NAME = "NEWS_BOARD_HISTORY_REQUEST";
const NEWS_BOARD_UPDATE_EVENT_NAME = "NEWS_BOARD_UPDATE";
const SUPPORT_MESSAGE_RECEIVE_EVENT_NAME = "SUPPORT_MESSAGE_RECEIVE";
const SUPPORT_MESSAGES_HISTORY_EVENT_NAME = "SUPPORT_MESSAGES_HISTORY";
const SUPPORT_MESSAGES_HISTORY_REQUEST_EVENT_NAME = "SUPPORT_MESSAGES_HISTORY_REQUEST";
const MOBILE_SIDEBAR_QUERY = "(max-width: 1024px) and (orientation: portrait)";
const LANDSCAPE_LAYOUT_QUERY = "(max-width: 1024px) and (orientation: landscape)";
const PHONE_PORTRAIT_QUERY = "(max-width: 820px) and (orientation: portrait)";
const COARSE_POINTER_QUERY = "(pointer: coarse)";
const MOBILE_TOOLTIP_DURATION_MS = 1800;
const DEFAULT_SIDEBAR_WIDTH = 300;
const DEFAULT_LANDSCAPE_SIDEBAR_WIDTH = 150;
const DEFAULT_MOBILE_LANDSCAPE_SIDEBAR_WIDTH = 92;
const ROUTE_SCROLL_RETRY_DELAY_MS = 140;
const ROUTE_SCROLL_MAX_RETRIES = 8;
const ROUTE_SCROLL_POST_RENDER_DELAY_MS = 80;
const USER_SCROLL_GUARD_MS = 260;
const PROGRAMMATIC_SCROLL_GUARD_MS = 420;
const XIMA_MOBILE_APP_FILE_NAME = "app xima.apk";
const XIMA_MOBILE_APP_DOWNLOAD_URL = `/${encodeURIComponent(XIMA_MOBILE_APP_FILE_NAME)}`;
const XIMA_MOBILE_APP_DOWNLOADED_STORAGE_PREFIX = "xima_mobile_app_downloaded_v2";
const normalizeAdminCandidate = (value) => String(value || "").trim().toLowerCase();
const CHAT_WIDGET_SCRIPT_ID = "xima-chat-widget-loader";
const CHATBOT_FALLBACK_HOSTS = [
  "https://juegoxima.com",
];
const ENABLE_XIMA_CHATBOT =
  String(process.env.REACT_APP_ENABLE_XIMA_CHATBOT || "0").trim() === "1";

const normalizeOrigin = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    if (/^\/\//.test(raw)) return `${window.location.protocol}${raw}`.replace(/\/+$/, "");
    if (/^https?:\/\//i.test(raw)) return new URL(raw).origin.replace(/\/+$/, "");
    if (/^[a-z0-9.-]+(?::\d+)?$/i.test(raw)) {
      return `${window.location.protocol}//${raw}`.replace(/\/+$/, "");
    }
    if (raw.startsWith("/")) return window.location.origin;
    if (raw.includes("://")) return String(new URL(raw).origin).replace(/\/+$/, "");
  } catch {
    return "";
  }
  return "";
};

const extractWidgetApiOrigin = () => {
  if (typeof window === "undefined") return "";
  const envCandidates = [
    process.env.REACT_APP_CHATBOT_API_URL,
    process.env.REACT_APP_CHAT_WIDGET_API,
    process.env.REACT_APP_API_URL,
  ];

  for (const raw of envCandidates) {
    const origin = normalizeOrigin(raw);
    if (origin) return origin;
  }

  if (window.location.port === "3000") {
    return `${window.location.protocol}//${window.location.hostname}:5001`;
  }

  return window.location.origin;
};

const buildWidgetSource = (rawSource) => {
  const raw = String(rawSource || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || /^\/\//.test(raw)) return raw;

  const origin = normalizeOrigin(raw);
  if (!origin) return "";
  return `${origin}/chatbot-widget/widget.js`;
};

const buildChatWidgetSources = () => {
  const candidateOrigins = [];
  const addSource = (raw, explicitApi = "") => {
    const source = buildWidgetSource(raw);
    if (!source) return;

    try {
      const parsedSource = new URL(source, window.location.href);
      const src = `${parsedSource.origin}/chatbot-widget/widget.js`;
      if (candidateOrigins.some((entry) => entry.src === src)) return;
      const fallbackApi = String(explicitApi || "").trim() || parsedSource.origin;
      candidateOrigins.push({
        src,
        api: normalizeOrigin(fallbackApi) || parsedSource.origin,
      });
    } catch {
      // Ignore invalid source candidates.
    }
  };

  const apiOrigin = extractWidgetApiOrigin();
  const explicitApi = normalizeOrigin(process.env.REACT_APP_CHATBOT_API_URL) || apiOrigin;

  addSource(process.env.REACT_APP_CHATBOT_WIDGET_SRC, explicitApi);
  addSource(process.env.REACT_APP_CHATBOT_WIDGET_URL, explicitApi);
  addSource(apiOrigin);
  addSource(window.location.origin);
  if (window.location.port === "3000") {
    addSource(`${window.location.protocol}//${window.location.hostname}:5001`);
  }
  CHATBOT_FALLBACK_HOSTS.forEach(addSource);

  return candidateOrigins;
};

const loadChatWidgetScript = (options = {}) => {
  if (typeof document === "undefined") return;
  const { force = false } = options;
  const existingScript = document.getElementById(CHAT_WIDGET_SCRIPT_ID);
  if (existingScript) {
    if (existingScript.dataset?.ximaLoadFailed === "true" || force) {
      existingScript.remove();
    } else {
      return;
    }
  }

  const apiBase = extractWidgetApiOrigin();
  const sources = buildChatWidgetSources();
  const scriptTag = document.createElement("script");
  scriptTag.id = CHAT_WIDGET_SCRIPT_ID;
  scriptTag.async = true;
  scriptTag.setAttribute("data-xima-chatbot", "true");
  scriptTag.setAttribute("data-position", "left-sidebar");
  if (apiBase) {
    scriptTag.setAttribute("data-api", apiBase);
  }

  const isLikelyJavaScript = async (source) => {
    if (!source) return false;
    if (!source.startsWith(window.location.origin)) return true;

    try {
      const response = await fetch(source, { method: "GET", cache: "no-store" });
      if (!response.ok) return false;
      const contentType = String(response.headers.get("content-type") || "").toLowerCase();
      return !contentType.includes("text/html");
    } catch {
      return false;
    }
  };

  const attemptLoad = async (index) => {
    if (index >= sources.length) {
      console.warn("[XIMA Chat] No se pudo cargar el widget desde los orígenes configurados:", sources);
      scriptTag.dataset.ximaLoadFailed = "true";
      if (scriptTag.parentNode) {
        scriptTag.parentNode.removeChild(scriptTag);
      }
      return;
    }

    const sourceEntry = sources[index];
    if (!sourceEntry || !sourceEntry.src) {
      return attemptLoad(index + 1);
    }

    const canLoad = await isLikelyJavaScript(sourceEntry.src);
    if (!canLoad) {
      return attemptLoad(index + 1);
    }

    scriptTag.setAttribute("data-api", sourceEntry.api || apiBase);
    scriptTag.src = sourceEntry.src;

    const watchdog = window.setTimeout(() => {
      if (!window.__XIMA_CHATBOT_API__ && !window.__XIMA_CHATBOT_LOADED__) {
        if (scriptTag.parentNode) {
          scriptTag.remove();
        }
        attemptLoad(index + 1);
      }
    }, 2500);

    scriptTag.onload = () => {
      clearTimeout(watchdog);
      scriptTag.onload = null;
      scriptTag.onerror = null;
      window.setTimeout(() => {
        if (window.__XIMA_CHATBOT_API__) {
          scriptTag.dataset.ximaLoadFailed = "false";
        } else {
          attemptLoad(index + 1);
        }
      }, 300);
    };

    scriptTag.onerror = () => {
      clearTimeout(watchdog);
      if (scriptTag.parentNode) {
        scriptTag.remove();
      }
      attemptLoad(index + 1);
    };

    if (scriptTag.parentNode) {
      scriptTag.parentNode.removeChild(scriptTag);
    }
    document.head.appendChild(scriptTag);
  };

  void attemptLoad(0);
};

const removeChatWidgetArtifacts = () => {
  if (typeof window !== "undefined") {
    window.__XIMA_CHATBOT_REQUEST_OPEN__ = false;
    window.__XIMA_CHATBOT_API__ = null;
    window.__XIMA_CHATBOT_LOADED__ = false;
  }

  if (typeof document === "undefined") return;

  const idsToRemove = [
    CHAT_WIDGET_SCRIPT_ID,
    "xima-chatbot-host",
    "xima-chatbot-styles",
  ];
  idsToRemove.forEach((id) => {
    const node = document.getElementById(id);
    if (node?.parentNode) {
      node.parentNode.removeChild(node);
    }
  });
};

const MENU_ICON_BY_KEY = {
  lobby: IoHomeOutline,
  championship: IoTrophyOutline,
  home: IoHomeOutline,
  news: IoNewspaperOutline,
  superusers: IoLockClosedOutline,
  learn: IoBookOutline,
  history: IoTimeOutline,
  stats: IoStatsChartOutline,
  settings: IoCogOutline,
  store: IoGiftOutline,
  watch: GiBinoculars,
  friends: IoPeopleOutline,
  donate: IoHeadsetOutline,
};
const LOBBY_SOURCE_PANEL_TITLE_KEY_MAP = {
  cube: "mobile.modeDesign",
  store: "mobile.modeStore",
  history: "mobile.modeHistory",
  friends: "mobile.modeFriends",
  learn: "mobile.modeLearn",
  spectate: "mobile.modeSpectate",
  settings: "mobile.modeSettings",
};

const GAME_MENU_ITEMS = [
  { key: "lobby", labelKey: "LOBY", route: "/game/lobby" },
  { key: "championship", labelKey: "CAMPEONATO", route: "/game/championship" },
  { key: "home", labelKey: "INICIO", route: "/game" },
  { key: "superusers", labelKey: "ADMIN", route: "/game?tab=admin" },
  { key: "learn", labelKey: "APRENDER", route: "/game/learn" },
  { key: "history", labelKey: "HISTORIAL", route: "/game/history" },
  { key: "stats", labelKey: "ESTADISTICAS", route: "/game/stats" },
  { key: "settings", labelKey: "AJUSTES", route: "/game/settings?section=general" },
  { key: "store", labelKey: "TIENDA", route: "/game/store" },
  { key: "watch", labelKey: "VER_PARTIDOS", route: "/game/spectate" },
  { key: "friends", labelKey: "AMIGOS", route: "/game/friends" },
  { key: "donate", labelKey: "DONAR", route: "/game/support" },
  { key: "news", labelKey: "NOTICIAS", route: "/game?tab=news" },
];

const getInitialLanguage = () => {
  if (typeof window === "undefined") return "en";
  const stored = String(window.localStorage.getItem("xima_ui_language") || "")
    .trim()
    .toLowerCase();
  if (["es", "en", "ru", "de"].includes(stored)) return stored;
  return detectBrowserLanguage();
};
const isMobileSidebarViewport = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(MOBILE_SIDEBAR_QUERY).matches;
};
const isLandscapeLayoutViewport = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(LANDSCAPE_LAYOUT_QUERY).matches;
};
const isLikelyPhoneDeviceViewport = () => {
  if (typeof window === "undefined" || typeof window.navigator === "undefined") return false;
  const navigatorRef = window.navigator;
  const userAgent = String(navigatorRef.userAgent || "");
  const isPhoneUa =
    /iPhone|iPod/i.test(userAgent) ||
    (/Android/i.test(userAgent) && /Mobile/i.test(userAgent)) ||
    navigatorRef.userAgentData?.mobile === true;
  if (!isPhoneUa) return false;
  const looksLikeTablet = /iPad|Tablet|Kindle|Silk|SM-T|Nexus 7|Nexus 10/i.test(userAgent);
  return !looksLikeTablet;
};
const isSamsungPortraitMobileViewport = () => {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function" ||
    typeof window.navigator === "undefined"
  ) {
    return false;
  }
  const userAgent = String(window.navigator.userAgent || "");
  const isSamsungAndroid =
    /Android/i.test(userAgent) && /(SamsungBrowser|SM-|SAMSUNG)/i.test(userAgent);
  if (!isSamsungAndroid) return false;
  return window.matchMedia(MOBILE_SIDEBAR_QUERY).matches;
};
const isIphonePortraitMobileViewport = () => {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function" ||
    typeof window.navigator === "undefined"
  ) {
    return false;
  }
  const userAgent = String(window.navigator.userAgent || "");
  const isIphone = /iPhone/i.test(userAgent);
  if (!isIphone) return false;
  return window.matchMedia(MOBILE_SIDEBAR_QUERY).matches;
};
const isIphoneOrSamsungDevice = () => {
  if (typeof window === "undefined" || typeof window.navigator === "undefined") return false;
  const userAgent = String(window.navigator.userAgent || "");
  const isIphone = /iPhone/i.test(userAgent);
  const isSamsungAndroid =
    /Android/i.test(userAgent) && /(SamsungBrowser|SM-|SAMSUNG)/i.test(userAgent);
  return isIphone || isSamsungAndroid;
};
const isIphoneSamsungLandscapeViewport = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  if (!isLikelyPhoneDeviceViewport()) return false;
  return isIphoneOrSamsungDevice() && window.matchMedia(LANDSCAPE_LAYOUT_QUERY).matches;
};
const isIphoneChromeLandscapeViewport = () => {
  if (
    typeof window === "undefined" ||
    typeof window.navigator === "undefined" ||
    !isLikelyPhoneDeviceViewport()
  ) {
    return false;
  }
  const userAgent = String(window.navigator.userAgent || "");
  const isIphoneChrome = /iPhone/i.test(userAgent) && /CriOS/i.test(userAgent);
  if (!isIphoneChrome) return false;
  const viewportWidth = Number(window.innerWidth) || 0;
  const viewportHeight = Number(window.innerHeight) || 0;
  return viewportWidth > viewportHeight;
};
const isSamsungChromeLandscapeViewport = () => {
  if (
    typeof window === "undefined" ||
    typeof window.navigator === "undefined" ||
    !isLikelyPhoneDeviceViewport()
  ) {
    return false;
  }
  const navigatorRef = window.navigator;
  const userAgent = String(navigatorRef.userAgent || "");
  const isAndroid = /Android/i.test(userAgent);
  const isChromeFamily = /Chrome/i.test(userAgent);
  const isSamsungBrowser = /SamsungBrowser/i.test(userAgent);
  const hasSamsungDeviceToken =
    /(SM-|SAMSUNG|Samsung|Galaxy|GT-|SCH-|SHV-|SGH-)/i.test(userAgent);
  const uaDataModel = String(navigatorRef.userAgentData?.model || "");
  const hasSamsungModelToken = /(SM-|SAMSUNG|Samsung|Galaxy)/i.test(uaDataModel);
  const isSamsungChrome =
    isAndroid &&
    isChromeFamily &&
    !isSamsungBrowser &&
    (hasSamsungDeviceToken || hasSamsungModelToken);
  if (!isSamsungChrome) return false;
  const viewportWidth = Number(window.innerWidth) || 0;
  const viewportHeight = Number(window.innerHeight) || 0;
  return viewportWidth > viewportHeight;
};
const isPhoneLandscapeByViewport = () => {
  if (typeof window === "undefined" || typeof window.navigator === "undefined") return false;
  if (!isLikelyPhoneDeviceViewport()) return false;
  const viewportWidth = Number(window.innerWidth) || 0;
  const viewportHeight = Number(window.innerHeight) || 0;
  return viewportWidth > viewportHeight;
};
const isPhoneLandscapeDesktopViewport = () =>
  isPhoneLandscapeByViewport() ||
  isIphoneChromeLandscapeViewport() ||
  isSamsungChromeLandscapeViewport();
const isPortraitPhoneViewport = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  if (!window.matchMedia(PHONE_PORTRAIT_QUERY).matches) return false;
  if (!window.matchMedia(COARSE_POINTER_QUERY).matches) return false;

  if (
    typeof document !== "undefined" &&
    document.body?.classList?.contains("xima-mobile-phone")
  ) {
    return true;
  }

  const userAgent = String(window.navigator?.userAgent || "");
  const looksLikeTablet = /iPad|Tablet|Kindle|Silk|SM-T|Nexus 7|Nexus 10/i.test(userAgent);
  return !looksLikeTablet;
};
const isNonPcOrLaptopDevice = () => {
  if (typeof window === "undefined" || typeof window.matchMedia === "function" === false) return false;

  const hasCoarsePointer =
    window.matchMedia(COARSE_POINTER_QUERY).matches ||
    window.matchMedia("(hover: none)").matches ||
    window.matchMedia("(any-pointer: coarse)").matches;
  if (hasCoarsePointer) return true;

  const viewportIsPhoneOrTabletWidth =
    Number(window.innerWidth) <= 1024 || window.matchMedia(MOBILE_SIDEBAR_QUERY).matches;
  if (viewportIsPhoneOrTabletWidth) return true;

  if (typeof window.navigator !== "undefined") {
    const userAgent = String(window.navigator.userAgent || "");
    const isMobileOrTabletAgent =
      /iPhone|iPod|iPad|Android|BlackBerry|webOS|Windows Phone|IEMobile|Opera Mini|Mobile|Tablet|Kindle|Silk|SM-T|Nexus 7|Nexus 10/i.test(
        userAgent
      );
    if (isMobileOrTabletAgent) return true;
    if (window.navigator.userAgentData?.mobile === true) return true;
  }

  return false;
};

const parseDateToMs = (value) => {
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const readStoredNumber = (storageKey) => {
  if (typeof window === "undefined") return 0;
  const parsed = Number(localStorage.getItem(storageKey) || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const writeStoredNumber = (storageKey, value) => {
  if (typeof window === "undefined") return;
  const normalized = Math.max(0, Number(value) || 0);
  localStorage.setItem(storageKey, String(normalized));
};

const readStoredString = (storageKey) => {
  if (typeof window === "undefined") return "";
  return String(localStorage.getItem(storageKey) || "").trim();
};

const writeStoredString = (storageKey, value) => {
  if (typeof window === "undefined") return;
  localStorage.setItem(storageKey, String(value || "").trim());
};

const getSeenSocialAlertTimestamp = () => {
  if (typeof window === "undefined") return 0;
  const raw = Number(localStorage.getItem(FRIEND_ALERT_SEEN_KEY) || 0);
  return Number.isFinite(raw) ? raw : 0;
};

const setSeenSocialAlertTimestamp = (value) => {
  if (typeof window === "undefined") return;
  const normalized = Math.max(0, Number(value) || 0);
  localStorage.setItem(FRIEND_ALERT_SEEN_KEY, String(normalized));
};

const GameLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const fromLobbySource = String(new URLSearchParams(location.search).get("fromLobbySource") || "")
    .trim()
    .toLowerCase();
  const lobbyModePanelTitle = fromLobbySource
    ? i18nT(LOBBY_SOURCE_PANEL_TITLE_KEY_MAP[fromLobbySource] || "mobile.modeGeneric", {
        source: fromLobbySource.toUpperCase(),
      })
    : "";
  const useDesktopLayoutForPhoneLandscape = isPhoneLandscapeDesktopViewport();
  const isLobbyMobileViewport =
    isMobileSidebarViewport() ||
    (!useDesktopLayoutForPhoneLandscape && isLandscapeLayoutViewport()) ||
    isIphonePortraitMobileViewport() ||
    isSamsungPortraitMobileViewport() ||
    (!useDesktopLayoutForPhoneLandscape &&
      (isIphoneChromeLandscapeViewport() || isSamsungChromeLandscapeViewport()));
  const isStorePanelFromLobbyMobile =
    fromLobbySource === "store" &&
    location.pathname === "/game/store" &&
    isLobbyMobileViewport;
  const isFriendsPanelFromLobbyMobile =
    fromLobbySource === "friends" &&
    location.pathname === "/game/friends" &&
    isLobbyMobileViewport;
  const isLearnRoute =
    location.pathname === "/game/learn" || location.pathname.startsWith("/game/learn/");
  const isLearnPanelFromLobby =
    fromLobbySource === "learn" && isLearnRoute;
  const isSettingsPanelFromLobbyMobile =
    fromLobbySource === "settings" &&
    location.pathname === "/game/settings" &&
    isLobbyMobileViewport;
  const showLobbySourcePanel =
    Boolean(fromLobbySource) &&
    location.pathname !== "/game/lobby" &&
    location.pathname !== "/game" &&
    !isStorePanelFromLobbyMobile &&
    !isFriendsPanelFromLobbyMobile &&
    !isLearnPanelFromLobby &&
    !isSettingsPanelFromLobbyMobile;

  const [language, setLanguageState] = useState(() => {
    const initialLanguage = getInitialLanguage();
    setI18nLanguage(initialLanguage);
    return initialLanguage;
  });
  const setLanguage = useCallback((nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setI18nLanguage(normalized);
    setLanguageState(normalized);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleLanguageChange = (event) => {
      const nextLanguage = normalizeLanguage(
        event?.detail?.language || window.localStorage.getItem("xima_ui_language")
      );
      setLanguageState((previousLanguage) =>
        previousLanguage === nextLanguage ? previousLanguage : nextLanguage
      );
    };

    window.addEventListener("xima:languagechange", handleLanguageChange);
    return () => window.removeEventListener("xima:languagechange", handleLanguageChange);
  }, []);
  const [layoutNavigationLocked, setLayoutNavigationLocked] = useState(false);
  const [socialAlert, setSocialAlert] = useState({
    incomingRequests: 0,
    incomingInvites: 0,
    pendingTotal: 0,
    newestPendingAt: 0,
    unseen: false,
  });
  const currentMenuAlertScope = useMemo(
    () =>
      normalizeAdminCandidate(
        user?.loginIdentifier ||
          user?.email ||
          user?.username ||
          user?.userId ||
          user?.id ||
          "guest"
      ) || "guest",
    [user?.email, user?.id, user?.loginIdentifier, user?.userId, user?.username]
  );
  const [newsAlertCount, setNewsAlertCount] = useState(0);
  const [supportAlertCount, setSupportAlertCount] = useState(0);
  const [learnAlertCount, setLearnAlertCount] = useState(0);
  const [storeAlertCount, setStoreAlertCount] = useState(0);
  const [adminPendingOrdersCount, setAdminPendingOrdersCount] = useState(0);
  const newsSeenAtRef = useRef(0);
  const supportSeenAtRef = useRef(0);
  const latestNewsAtRef = useRef(0);
  const latestSupportAtRef = useRef(0);
  const newsKnownIdsRef = useRef(new Set());
  const supportKnownIdsRef = useRef(new Set());
  const lastManualScrollAtRef = useRef(0);
  const programmaticScrollUntilRef = useRef(0);
  const autoScrollTimeoutRef = useRef(null);
  const autoScrollRafRef = useRef(null);
  const autoScrollRetryCountRef = useRef(0);
  const pendingAutoScrollRouteKeyRef = useRef("");
  const lastAutoScrolledRouteKeyRef = useRef("");
  const [isMobileSidebar, setIsMobileSidebar] = useState(isMobileSidebarViewport);
  const [isLandscapeLayout, setIsLandscapeLayout] = useState(
    () => !isPhoneLandscapeDesktopViewport() && isLandscapeLayoutViewport()
  );
  const [isIphoneChromeLandscape, setIsIphoneChromeLandscape] = useState(false);
  const [isSamsungChromeLandscape, setIsSamsungChromeLandscape] = useState(false);
  const [isPhoneLandscapeViewport, setIsPhoneLandscapeViewport] = useState(false);
  const [isSamsungPortraitMobile, setIsSamsungPortraitMobile] = useState(
    isSamsungPortraitMobileViewport
  );
  const [isIphonePortraitMobile, setIsIphonePortraitMobile] = useState(
    isIphonePortraitMobileViewport
  );
  const [isIphoneSamsungLandscape, setIsIphoneSamsungLandscape] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    if (isPhoneLandscapeDesktopViewport()) {
      return DEFAULT_SIDEBAR_WIDTH;
    }
    if (isIphoneSamsungLandscapeViewport()) {
      return DEFAULT_MOBILE_LANDSCAPE_SIDEBAR_WIDTH;
    }
    return isLandscapeLayoutViewport() ? DEFAULT_LANDSCAPE_SIDEBAR_WIDTH : DEFAULT_SIDEBAR_WIDTH;
  });
  const [mobileTooltip, setMobileTooltip] = useState({
    key: null,
    label: "",
    x: 0,
    y: 0,
  });
  const mobileTooltipTimeoutRef = useRef(null);
  const isSidebarCollapsed = isMobileSidebar || isDesktopSidebarCollapsed;
  const isPhoneLandscapeLayout =
    isLandscapeLayout ||
    isIphoneChromeLandscape ||
    isSamsungChromeLandscape ||
    isPhoneLandscapeViewport;
  const shouldBlockPhoneLandscape = false;
  const phoneLandscapeBlockCopy = useMemo(() => {
    const copyByLanguage = {
      es: {
        title: "GIRA TU TELÉFONO",
        line1: "Este juego está optimizado para usarse en posición vertical.",
        line2: "Actualmente estamos trabajando para mejorar la experiencia en modo horizontal.",
      },
      en: {
        title: "ROTATE YOUR PHONE",
        line1: "This game is optimized for use in portrait mode.",
        line2: "We are currently working to improve the experience in landscape mode.",
      },
      ru: {
        title: "ПОВЕРНИТЕ ТЕЛЕФОН",
        line1: "Эта игра оптимизирована для использования в вертикальной ориентации.",
        line2: "Сейчас мы работаем над улучшением работы в горизонтальном режиме.",
      },
      de: {
        title: "DREHEN SIE IHR TELEFON",
        line1: "Dieses Spiel ist für die Nutzung im Hochformat optimiert.",
        line2: "Wir arbeiten derzeit daran, das Erlebnis im Querformat zu verbessern.",
      },
    };
    return copyByLanguage[normalizeLanguage(language)] || copyByLanguage.en;
  }, [language]);
  const isSidebarRouteActive = useCallback(
    (menuKey) => {
      const normalizedMenuKey = String(menuKey || "").trim().toLowerCase();
      if (normalizedMenuKey === "news") {
        if (location.pathname !== "/game") return false;
        const tab = String(new URLSearchParams(location.search).get("tab") || "")
          .trim()
          .toLowerCase();
        return tab === "news";
      }
      if (normalizedMenuKey === "donate") {
        if (location.pathname === "/game/donate" || location.pathname === "/game/support") return true;
        if (!location.pathname.startsWith("/game/settings")) return false;
        return (
          String(new URLSearchParams(location.search).get("section") || "")
            .trim()
            .toLowerCase() === "support"
        );
      }
      return false;
    },
    [location.pathname, location.search]
  );

  const hasAdminMenuAccess = useMemo(() => {
    const roleCandidates = [
      user?.role,
      user?.type,
      user?.permission,
      user?.accountType,
      user?.accessLevel,
    ].map(normalizeAdminCandidate);
    const hasAdminRole = roleCandidates.some(
      (value) => value === "admin" || value === "superuser"
    );

    return hasAdminRole;
  }, [user]);
  const resolveScopedAlertStorageKey = useCallback(
    (baseKey) => `${baseKey}:${currentMenuAlertScope}`,
    [currentMenuAlertScope]
  );
  const mobileAppDownloadedStorageKey = useMemo(
    () => resolveScopedAlertStorageKey(XIMA_MOBILE_APP_DOWNLOADED_STORAGE_PREFIX),
    [resolveScopedAlertStorageKey]
  );
  const [hasDownloadedXimaMobileApp, setHasDownloadedXimaMobileApp] = useState(false);
  const [isMobileAppDownloadPromptOpen, setIsMobileAppDownloadPromptOpen] = useState(false);

  useEffect(() => {
    setHasDownloadedXimaMobileApp(readStoredString(mobileAppDownloadedStorageKey) === "1");
  }, [mobileAppDownloadedStorageKey]);

  const countUnseenNewsFromHistory = useCallback((entries, seenAt) => {
    const source = Array.isArray(entries) ? entries : [];
    return source.reduce((total, entry) => {
      const createdAtMs = parseDateToMs(
        entry?.createdAt || entry?.updatedAt || entry?.timestamp || 0
      );
      if (!createdAtMs || createdAtMs <= seenAt) return total;
      return total + 1;
    }, 0);
  }, []);
  const countUnseenSupportFromHistory = useCallback((entries, seenAt) => {
    const source = Array.isArray(entries) ? entries : [];
    return source.reduce((total, entry) => {
      const messageAt = Number(entry?.timestamp) || 0;
      if (!messageAt || messageAt <= seenAt) return total;
      return total + 1;
    }, 0);
  }, []);
  const markSidebarMenuBadgeAsSeen = useCallback(
    (menuKey) => {
      const normalizedMenuKey = String(menuKey || "").trim().toLowerCase();
      if (normalizedMenuKey === "news") {
        const nextSeenAt = Math.max(0, Number(latestNewsAtRef.current) || 0);
        newsSeenAtRef.current = nextSeenAt;
        writeStoredNumber(resolveScopedAlertStorageKey(NEWS_ALERT_SEEN_KEY), nextSeenAt);
        setNewsAlertCount(0);
        return;
      }
      if (normalizedMenuKey === "donate") {
        const nextSeenAt = Math.max(0, Number(latestSupportAtRef.current) || 0);
        supportSeenAtRef.current = nextSeenAt;
        writeStoredNumber(resolveScopedAlertStorageKey(SUPPORT_ALERT_SEEN_KEY), nextSeenAt);
        setSupportAlertCount(0);
        return;
      }
      if (normalizedMenuKey === "learn") {
        writeStoredString(
          resolveScopedAlertStorageKey(LEARN_ALERT_SEEN_VERSION_KEY),
          LEARN_CONTENT_VERSION
        );
        setLearnAlertCount(0);
        return;
      }
      if (normalizedMenuKey === "store") {
        writeStoredNumber(
          resolveScopedAlertStorageKey(STORE_ALERT_SEEN_COUNT_KEY),
          STORE_PRODUCTS_TOTAL_COUNT
        );
        setStoreAlertCount(0);
      }
    },
    [resolveScopedAlertStorageKey]
  );
  const getSidebarMenuBadgeCount = useCallback(
    (menuKey) => {
      const normalizedMenuKey = String(menuKey || "").trim().toLowerCase();
      if (!SIDEBAR_BADGE_KEYS.has(normalizedMenuKey)) return 0;
      if (normalizedMenuKey === "friends") {
        return Math.max(0, Number(socialAlert.pendingTotal) || 0);
      }
      if (normalizedMenuKey === "news") return Math.max(0, Number(newsAlertCount) || 0);
      if (normalizedMenuKey === "learn") return Math.max(0, Number(learnAlertCount) || 0);
      if (normalizedMenuKey === "store") return Math.max(0, Number(storeAlertCount) || 0);
      if (normalizedMenuKey === "donate") return Math.max(0, Number(supportAlertCount) || 0);
      if (normalizedMenuKey === "superusers") {
        return hasAdminMenuAccess ? Math.max(0, Number(adminPendingOrdersCount) || 0) : 0;
      }
      return 0;
    },
    [
      adminPendingOrdersCount,
      hasAdminMenuAccess,
      learnAlertCount,
      newsAlertCount,
      socialAlert.pendingTotal,
      storeAlertCount,
      supportAlertCount,
    ]
  );
  const playSidebarButtonSound = useCallback(() => {
    if (typeof window === "undefined") return;
    if (window.XimaSoundManager?.playRandomButtonSound) {
      window.XimaSoundManager.playRandomButtonSound();
      return;
    }
    window.XimaSoundManager?.playClick?.();
  }, []);
  const handleSidebarButtonSoundCapture = useCallback(
    (event) => {
      if (layoutNavigationLocked) return;
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest("button");
      if (!button) return;
      if (button.disabled || button.getAttribute("aria-disabled") === "true") return;
      const nativeEvent = event?.nativeEvent || event;
      if (nativeEvent?.__ximaSoundHandled) return;
      if (nativeEvent) {
        nativeEvent.__ximaSoundHandled = true;
      }
      playSidebarButtonSound();
    },
    [layoutNavigationLocked, playSidebarButtonSound]
  );
  const openXimaAssistant = useCallback(() => {
    if (!ENABLE_XIMA_CHATBOT) return;
    if (typeof window === "undefined") return;

    const tryOpenChatbot = () => {
      const chatbotApi = window.__XIMA_CHATBOT_API__;
      if (chatbotApi && typeof chatbotApi.open === "function") {
        chatbotApi.open();
        window.__XIMA_CHATBOT_REQUEST_OPEN__ = false;
        return true;
      }

      const launchButton = typeof document !== "undefined"
        ? document.getElementById("xima-chatbot-toggle")
        : null;
      if (launchButton instanceof Element) {
        launchButton.dispatchEvent(new MouseEvent("click", { bubbles: true }));
        return true;
      }

      return false;
    };

    if (tryOpenChatbot()) return;

    window.__XIMA_CHATBOT_REQUEST_OPEN__ = true;
    window.dispatchEvent(new CustomEvent("xima-chatbot-open"));
    window.dispatchEvent(new CustomEvent("xima-chat-open"));
    if (typeof document !== "undefined") {
      const scriptExists = Boolean(document.getElementById(CHAT_WIDGET_SCRIPT_ID));
      loadChatWidgetScript({ force: !scriptExists || !window.__XIMA_CHATBOT_API__ });
    }

    let attempts = 0;
    const maxAttempts = 18;
    const retryDelayMs = 350;
    const retryTimer = setInterval(() => {
      attempts += 1;
      if (tryOpenChatbot()) {
        window.__XIMA_CHATBOT_REQUEST_OPEN__ = false;
        clearInterval(retryTimer);
        return;
      }

      if (attempts >= maxAttempts) {
        window.__XIMA_CHATBOT_REQUEST_OPEN__ = false;
        clearInterval(retryTimer);
        console.warn(
          "[XIMA Chat] No se pudo abrir el asistente desde el botón. Revisa que /chatbot-widget/widget.js y /chat o /api/chat estén disponibles."
        );
      }
    }, retryDelayMs);

    setTimeout(() => clearInterval(retryTimer), 7000);
  }, []);

  const clearMobileTooltipTimeout = useCallback(() => {
    if (!mobileTooltipTimeoutRef.current) return;
    clearTimeout(mobileTooltipTimeoutRef.current);
    mobileTooltipTimeoutRef.current = null;
  }, []);
  const hideMobileTooltip = useCallback(() => {
    clearMobileTooltipTimeout();
    setMobileTooltip((previousTooltip) => {
      if (!previousTooltip.key) return previousTooltip;
      return {
        key: null,
        label: "",
        x: 0,
        y: 0,
      };
    });
  }, [clearMobileTooltipTimeout]);
  const showMobileTooltip = useCallback(
    (event, key, label) => {
      if (!isMobileSidebar || !isSidebarCollapsed) return;
      const currentTarget = event?.currentTarget;
      if (!(currentTarget instanceof Element)) return;
      const rect = currentTarget.getBoundingClientRect();
      const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 320;
      const tooltipCenterX = Math.min(
        Math.max(rect.left + rect.width / 2, 68),
        Math.max(68, viewportWidth - 68)
      );
      const tooltipTop = Math.max(10, rect.top - 6);
      clearMobileTooltipTimeout();
      setMobileTooltip({
        key,
        label,
        x: Math.round(tooltipCenterX),
        y: Math.round(tooltipTop),
      });
      mobileTooltipTimeoutRef.current = setTimeout(() => {
        setMobileTooltip({
          key: null,
          label: "",
          x: 0,
          y: 0,
        });
        mobileTooltipTimeoutRef.current = null;
      }, MOBILE_TOOLTIP_DURATION_MS);
    },
    [clearMobileTooltipTimeout, isMobileSidebar, isSidebarCollapsed]
  );
  const runSidebarActionWithMobileTooltip = useCallback(
    (event, key, label, action) => {
      if (layoutNavigationLocked) return;
      if (isMobileSidebar && isSidebarCollapsed) {
        showMobileTooltip(event, key, label);
      }
      action();
    },
    [isMobileSidebar, isSidebarCollapsed, layoutNavigationLocked, showMobileTooltip]
  );
  const getSidebarResizeBounds = useCallback(() => {
    const viewportWidth =
      typeof window !== "undefined" ? Math.max(640, Number(window.innerWidth) || 0) : 1280;
    const useDesktopBoundsForPhoneLandscape = isPhoneLandscapeDesktopViewport();
    if (!useDesktopBoundsForPhoneLandscape && isIphoneSamsungLandscapeViewport()) {
      const fixedWidth = DEFAULT_MOBILE_LANDSCAPE_SIDEBAR_WIDTH;
      return { minWidth: fixedWidth, maxWidth: fixedWidth };
    }
    if (!useDesktopBoundsForPhoneLandscape && isLandscapeLayoutViewport()) {
      const minWidth = 110;
      const maxWidth = Math.max(minWidth + 20, Math.min(280, Math.floor(viewportWidth * 0.34)));
      return { minWidth, maxWidth };
    }
    const minWidth = 180;
    const maxWidth = Math.max(minWidth + 20, Math.min(520, Math.floor(viewportWidth * 0.45)));
    return { minWidth, maxWidth };
  }, []);
  const handleSidebarResizeStart = useCallback(
    (event) => {
      if (
        isSidebarCollapsed ||
        isMobileSidebar ||
        layoutNavigationLocked ||
        isIphoneSamsungLandscape
      ) {
        return;
      }
      if (typeof window === "undefined") return;
      event.preventDefault();
      const startX = Number(event.clientX) || 0;
      const startWidth = Number(sidebarWidth) || DEFAULT_SIDEBAR_WIDTH;
      const { minWidth, maxWidth } = getSidebarResizeBounds();

      const handlePointerMove = (moveEvent) => {
        const deltaX = Number(moveEvent.clientX) - startX;
        const nextWidth = Math.min(Math.max(startWidth + deltaX, minWidth), maxWidth);
        setSidebarWidth(Math.round(nextWidth));
      };
      const handlePointerUp = () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [
      getSidebarResizeBounds,
      isMobileSidebar,
      isSidebarCollapsed,
      isIphoneSamsungLandscape,
      layoutNavigationLocked,
      sidebarWidth,
    ]
  );

  useEffect(() => {
    setStoredLanguage(language);
  }, [language]);

  useEffect(() => {
    if (!ENABLE_XIMA_CHATBOT) {
      removeChatWidgetArtifacts();
      return;
    }
    loadChatWidgetScript();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mobileQuery = window.matchMedia(MOBILE_SIDEBAR_QUERY);
    const landscapeQuery = window.matchMedia(LANDSCAPE_LAYOUT_QUERY);
    const handleViewportChange = () => {
      const isMobile = Boolean(mobileQuery.matches);
      const isLandscape = Boolean(landscapeQuery.matches);
      const useDesktopLayoutForPhoneLandscapeNow = isPhoneLandscapeDesktopViewport();
      const effectiveLandscapeLayout = useDesktopLayoutForPhoneLandscapeNow ? false : isLandscape;
      const isIphoneChromeLandscapeNow = useDesktopLayoutForPhoneLandscapeNow
        ? false
        : isIphoneChromeLandscapeViewport();
      const isSamsungChromeLandscapeNow = useDesktopLayoutForPhoneLandscapeNow
        ? false
        : isSamsungChromeLandscapeViewport();
      const isPhoneLandscapeViewportNow = useDesktopLayoutForPhoneLandscapeNow
        ? false
        : isPhoneLandscapeByViewport();
      const isIphoneSamsungLandscapeNow = useDesktopLayoutForPhoneLandscapeNow
        ? false
        : isIphoneSamsungLandscapeViewport();
      setIsMobileSidebar(isMobile);
      setIsLandscapeLayout(effectiveLandscapeLayout);
      setIsIphoneChromeLandscape(isIphoneChromeLandscapeNow);
      setIsSamsungChromeLandscape(isSamsungChromeLandscapeNow);
      setIsPhoneLandscapeViewport(isPhoneLandscapeViewportNow);
      setIsSamsungPortraitMobile(isSamsungPortraitMobileViewport());
      setIsIphonePortraitMobile(isIphonePortraitMobileViewport());
      setIsIphoneSamsungLandscape(isIphoneSamsungLandscapeNow);
      if (
        effectiveLandscapeLayout ||
        isIphoneChromeLandscapeNow ||
        isSamsungChromeLandscapeNow ||
        isPhoneLandscapeViewportNow
      ) {
        setIsDesktopSidebarCollapsed(false);
      }
      setSidebarWidth((previousWidth) => {
        const { minWidth, maxWidth } = getSidebarResizeBounds();
        const fallbackWidth = effectiveLandscapeLayout
          ? isIphoneSamsungLandscapeNow
            ? DEFAULT_MOBILE_LANDSCAPE_SIDEBAR_WIDTH
            : DEFAULT_LANDSCAPE_SIDEBAR_WIDTH
          : DEFAULT_SIDEBAR_WIDTH;
        const normalized = Number.isFinite(previousWidth)
          ? Number(previousWidth)
          : fallbackWidth;
        return Math.round(Math.min(Math.max(normalized, minWidth), maxWidth));
      });
      hideMobileTooltip();
    };
    handleViewportChange();

    if (
      typeof mobileQuery.addEventListener === "function" &&
      typeof landscapeQuery.addEventListener === "function"
    ) {
      mobileQuery.addEventListener("change", handleViewportChange);
      landscapeQuery.addEventListener("change", handleViewportChange);
      window.addEventListener("resize", handleViewportChange);
      return () => {
        mobileQuery.removeEventListener("change", handleViewportChange);
        landscapeQuery.removeEventListener("change", handleViewportChange);
        window.removeEventListener("resize", handleViewportChange);
      };
    }
    mobileQuery.addListener(handleViewportChange);
    landscapeQuery.addListener(handleViewportChange);
    window.addEventListener("resize", handleViewportChange);
    return () => {
      mobileQuery.removeListener(handleViewportChange);
      landscapeQuery.removeListener(handleViewportChange);
      window.removeEventListener("resize", handleViewportChange);
    };
  }, [getSidebarResizeBounds, hideMobileTooltip]);

  useEffect(() => () => clearMobileTooltipTimeout(), [clearMobileTooltipTimeout]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (!isMobileSidebar || !mobileTooltip.key) return undefined;
    const handleOutsideTap = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest("[data-sidebar-tooltip-trigger='true']")) return;
      hideMobileTooltip();
    };
    document.addEventListener("pointerdown", handleOutsideTap);
    return () => document.removeEventListener("pointerdown", handleOutsideTap);
  }, [hideMobileTooltip, isMobileSidebar, mobileTooltip.key]);

  const fetchSocialAlert = useCallback(async () => {
    try {
      const [requestsResponse, invitesResponse] = await Promise.all([
        axios.get("/friends/requests"),
        axios.get("/friends/invites"),
      ]);

      const incomingRequests = Array.isArray(requestsResponse?.data?.incoming)
        ? requestsResponse.data.incoming
        : [];
      const incomingInvites = Array.isArray(invitesResponse?.data?.incoming)
        ? invitesResponse.data.incoming
        : [];
      const pendingTotal = incomingRequests.length + incomingInvites.length;
      const newestPendingAt = Math.max(
        ...incomingRequests.map((item) => parseDateToMs(item?.createdAt)),
        ...incomingInvites.map((item) => parseDateToMs(item?.createdAt)),
        0
      );
      const seenAt = getSeenSocialAlertTimestamp();
      const unseen = pendingTotal > 0 && newestPendingAt > seenAt;

      setSocialAlert({
        incomingRequests: incomingRequests.length,
        incomingInvites: incomingInvites.length,
        pendingTotal,
        newestPendingAt,
        unseen,
      });
    } catch (error) {
      setSocialAlert((prev) => ({
        ...prev,
        unseen: false,
      }));
    }
  }, []);

  useEffect(() => {
    fetchSocialAlert().catch((error) => console.log(error));
    const intervalId = setInterval(() => {
      fetchSocialAlert().catch((error) => console.log(error));
    }, 12000);
    return () => clearInterval(intervalId);
  }, [fetchSocialAlert]);

  const fetchAdminPendingOrdersCount = useCallback(async () => {
    if (!hasAdminMenuAccess) {
      setAdminPendingOrdersCount(0);
      return;
    }

    try {
      const response = await axios.get("/admin/orders/pending");
      const totalFromApi = Number(response?.data?.total);
      const pendingOrders = Array.isArray(response?.data?.orders) ? response.data.orders : [];
      const resolvedCount = Number.isFinite(totalFromApi) ? totalFromApi : pendingOrders.length;
      setAdminPendingOrdersCount(Math.max(0, Math.round(resolvedCount || 0)));
    } catch (error) {
      setAdminPendingOrdersCount(0);
    }
  }, [hasAdminMenuAccess]);

  useEffect(() => {
    fetchAdminPendingOrdersCount().catch((error) => console.log(error));
    if (!hasAdminMenuAccess) return undefined;
    const intervalId = setInterval(() => {
      fetchAdminPendingOrdersCount().catch((error) => console.log(error));
    }, 15000);
    return () => clearInterval(intervalId);
  }, [fetchAdminPendingOrdersCount, hasAdminMenuAccess]);

  useEffect(() => {
    const newsSeenAt = readStoredNumber(resolveScopedAlertStorageKey(NEWS_ALERT_SEEN_KEY));
    const supportSeenAt = readStoredNumber(resolveScopedAlertStorageKey(SUPPORT_ALERT_SEEN_KEY));
    const learnVersionStorageKey = resolveScopedAlertStorageKey(LEARN_ALERT_SEEN_VERSION_KEY);
    const storeCountStorageKey = resolveScopedAlertStorageKey(STORE_ALERT_SEEN_COUNT_KEY);
    const seenLearnVersion = readStoredString(learnVersionStorageKey);
    const hasSeenLearnVersion = typeof window !== "undefined" && localStorage.getItem(learnVersionStorageKey) !== null;
    const seenStoreCountRaw = typeof window !== "undefined" ? localStorage.getItem(storeCountStorageKey) : null;
    const hasSeenStoreCount = seenStoreCountRaw !== null;
    const seenStoreCount = hasSeenStoreCount ? readStoredNumber(storeCountStorageKey) : STORE_PRODUCTS_TOTAL_COUNT;

    newsSeenAtRef.current = newsSeenAt;
    supportSeenAtRef.current = supportSeenAt;
    newsKnownIdsRef.current = new Set();
    supportKnownIdsRef.current = new Set();
    latestNewsAtRef.current = newsSeenAt;
    latestSupportAtRef.current = supportSeenAt;
    setNewsAlertCount(0);
    setSupportAlertCount(0);
    if (!hasSeenLearnVersion) {
      writeStoredString(learnVersionStorageKey, LEARN_CONTENT_VERSION);
      setLearnAlertCount(0);
    } else {
      setLearnAlertCount(seenLearnVersion === LEARN_CONTENT_VERSION ? 0 : 1);
    }
    if (!hasSeenStoreCount) {
      writeStoredNumber(storeCountStorageKey, STORE_PRODUCTS_TOTAL_COUNT);
      setStoreAlertCount(0);
      return;
    }
    setStoreAlertCount(Math.max(0, STORE_PRODUCTS_TOTAL_COUNT - seenStoreCount));
  }, [resolveScopedAlertStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let activeSocket = null;
    let retryTimer = null;
    let disposed = false;
    const fallbackUserId = normalizeAdminCandidate(
      user?.loginIdentifier || user?.email || user?.username || user?.userId || user?.id || ""
    );

    const normalizeNewsEntry = (rawPayload = {}) => {
      const id = String(rawPayload?.id || rawPayload?.messageId || "").trim();
      const createdAtMs = parseDateToMs(
        rawPayload?.createdAt || rawPayload?.updatedAt || rawPayload?.timestamp || 0
      );
      return { id, createdAtMs };
    };

    const normalizeSupportEntry = (rawPayload = {}) => {
      const id = String(rawPayload?.id || rawPayload?.messageId || "").trim();
      const timestamp = Math.max(0, Number(rawPayload?.timestamp) || 0);
      return { id, timestamp };
    };

    const bindSocketListeners = (socket) => {
      if (!socket || typeof socket.on !== "function") return false;
      activeSocket = socket;

      const handleNewsHistory = (payload = []) => {
        const source = Array.isArray(payload) ? payload : [];
        const uniqueIds = new Set();
        const normalizedEntries = source.map((entry) => normalizeNewsEntry(entry));
        normalizedEntries.forEach((entry) => {
          if (entry.id) uniqueIds.add(entry.id);
          latestNewsAtRef.current = Math.max(
            latestNewsAtRef.current,
            Number(entry.createdAtMs) || 0
          );
        });
        newsKnownIdsRef.current = uniqueIds;
        setNewsAlertCount(
          countUnseenNewsFromHistory(source, Number(newsSeenAtRef.current) || 0)
        );
      };

      const handleNewsMessage = (payload = {}) => {
        const entry = normalizeNewsEntry(payload);
        if (!entry.createdAtMs) return;
        if (entry.id && newsKnownIdsRef.current.has(entry.id)) return;
        if (entry.id) newsKnownIdsRef.current.add(entry.id);
        latestNewsAtRef.current = Math.max(latestNewsAtRef.current, entry.createdAtMs);
        if (isSidebarRouteActive("news")) {
          newsSeenAtRef.current = Math.max(newsSeenAtRef.current, entry.createdAtMs);
          writeStoredNumber(
            resolveScopedAlertStorageKey(NEWS_ALERT_SEEN_KEY),
            newsSeenAtRef.current
          );
          setNewsAlertCount(0);
          return;
        }
        if (entry.createdAtMs <= (Number(newsSeenAtRef.current) || 0)) return;
        setNewsAlertCount((previous) => Math.max(0, Number(previous) || 0) + 1);
      };

      const handleNewsUpdated = (payload = {}) => {
        const entry = normalizeNewsEntry(payload);
        if (!entry.createdAtMs) return;
        latestNewsAtRef.current = Math.max(latestNewsAtRef.current, entry.createdAtMs);
        if (isSidebarRouteActive("news")) {
          newsSeenAtRef.current = Math.max(newsSeenAtRef.current, entry.createdAtMs);
          writeStoredNumber(
            resolveScopedAlertStorageKey(NEWS_ALERT_SEEN_KEY),
            newsSeenAtRef.current
          );
          setNewsAlertCount(0);
          return;
        }
        if (entry.createdAtMs <= (Number(newsSeenAtRef.current) || 0)) return;
        setNewsAlertCount((previous) => Math.max(0, Number(previous) || 0) + 1);
      };

      const handleSupportHistory = (payload = []) => {
        const source = Array.isArray(payload) ? payload : [];
        const uniqueIds = new Set();
        const normalizedEntries = source.map((entry) => normalizeSupportEntry(entry));
        normalizedEntries.forEach((entry) => {
          if (entry.id) uniqueIds.add(entry.id);
          latestSupportAtRef.current = Math.max(
            latestSupportAtRef.current,
            Number(entry.timestamp) || 0
          );
        });
        supportKnownIdsRef.current = uniqueIds;
        setSupportAlertCount(
          countUnseenSupportFromHistory(source, Number(supportSeenAtRef.current) || 0)
        );
      };

      const handleSupportMessage = (payload = {}) => {
        const entry = normalizeSupportEntry(payload);
        if (!entry.timestamp) return;
        if (entry.id && supportKnownIdsRef.current.has(entry.id)) return;
        if (entry.id) supportKnownIdsRef.current.add(entry.id);
        latestSupportAtRef.current = Math.max(latestSupportAtRef.current, entry.timestamp);
        if (isSidebarRouteActive("donate")) {
          supportSeenAtRef.current = Math.max(supportSeenAtRef.current, entry.timestamp);
          writeStoredNumber(
            resolveScopedAlertStorageKey(SUPPORT_ALERT_SEEN_KEY),
            supportSeenAtRef.current
          );
          setSupportAlertCount(0);
          return;
        }
        if (entry.timestamp <= (Number(supportSeenAtRef.current) || 0)) return;
        setSupportAlertCount((previous) => Math.max(0, Number(previous) || 0) + 1);
      };

      if (typeof socket.off === "function") {
        socket.off(NEWS_BOARD_HISTORY_EVENT_NAME, handleNewsHistory);
        socket.off(NEWS_BOARD_EVENT_NAME, handleNewsMessage);
        socket.off(NEWS_BOARD_UPDATE_EVENT_NAME, handleNewsUpdated);
        socket.off(SUPPORT_MESSAGES_HISTORY_EVENT_NAME, handleSupportHistory);
        socket.off(SUPPORT_MESSAGE_RECEIVE_EVENT_NAME, handleSupportMessage);
      }

      socket.on(NEWS_BOARD_HISTORY_EVENT_NAME, handleNewsHistory);
      socket.on(NEWS_BOARD_EVENT_NAME, handleNewsMessage);
      socket.on(NEWS_BOARD_UPDATE_EVENT_NAME, handleNewsUpdated);
      socket.on(SUPPORT_MESSAGES_HISTORY_EVENT_NAME, handleSupportHistory);
      socket.on(SUPPORT_MESSAGE_RECEIVE_EVENT_NAME, handleSupportMessage);
      if (typeof socket.emit === "function") {
        socket.emit(NEWS_BOARD_HISTORY_REQUEST_EVENT_NAME);
        if (fallbackUserId) {
          socket.emit(SUPPORT_MESSAGES_HISTORY_REQUEST_EVENT_NAME, { userId: fallbackUserId });
        }
      }

      return () => {
        if (typeof socket.off === "function") {
          socket.off(NEWS_BOARD_HISTORY_EVENT_NAME, handleNewsHistory);
          socket.off(NEWS_BOARD_EVENT_NAME, handleNewsMessage);
          socket.off(NEWS_BOARD_UPDATE_EVENT_NAME, handleNewsUpdated);
          socket.off(SUPPORT_MESSAGES_HISTORY_EVENT_NAME, handleSupportHistory);
          socket.off(SUPPORT_MESSAGE_RECEIVE_EVENT_NAME, handleSupportMessage);
        }
      };
    };

    let unbindListeners = () => {};
    const tryAttach = () => {
      if (disposed) return;
      const socketCandidate = window.__ximaSocket || window.socket || null;
      if (!socketCandidate) return;
      if (activeSocket === socketCandidate) return;
      unbindListeners();
      const maybeUnbind = bindSocketListeners(socketCandidate);
      if (typeof maybeUnbind === "function") {
        unbindListeners = maybeUnbind;
      }
    };

    tryAttach();
    retryTimer = window.setInterval(tryAttach, 1500);

    return () => {
      disposed = true;
      if (retryTimer) clearInterval(retryTimer);
      unbindListeners();
      activeSocket = null;
    };
  }, [
    countUnseenNewsFromHistory,
    countUnseenSupportFromHistory,
    currentMenuAlertScope,
    isSidebarRouteActive,
    resolveScopedAlertStorageKey,
    user?.email,
    user?.id,
    user?.loginIdentifier,
    user?.userId,
    user?.username,
  ]);

  const isCompactDeviceForMenu =
    !useDesktopLayoutForPhoneLandscape && isNonPcOrLaptopDevice();
  const localizedMenuItems = GAME_MENU_ITEMS.map((item) => ({
    ...item,
    label:
      isMobileSidebar && item.key === "lobby" ? t("INICIO") : t(item.labelKey),
  })).filter((item) => item.key !== "home");
  const reorderedMobileMenuItems = isCompactDeviceForMenu
    ? (() => {
        const items = [...localizedMenuItems];
        const lobbyIndex = items.findIndex((item) => item.key === "lobby");
        const donateIndex = items.findIndex((item) => item.key === "donate");
        const friendsIndex = items.findIndex((item) => item.key === "friends");

        if (lobbyIndex > -1 && donateIndex > -1 && friendsIndex > -1) {
          items.splice(lobbyIndex, 1);
          const nextDonateIndex =
            donateIndex > lobbyIndex
              ? Math.max(donateIndex - 1, 0)
              : donateIndex;
          const safeInsertIndex = Math.min(nextDonateIndex + 1, items.length);
          const nextFriendsIndex =
            items.findIndex((item) => item.key === "friends");
          if (nextFriendsIndex === -1 || safeInsertIndex <= nextFriendsIndex) {
            items.splice(safeInsertIndex, 0, {
              ...localizedMenuItems[lobbyIndex],
            });
          } else if (items.length > 0) {
            items.splice(nextFriendsIndex, 0, {
              ...localizedMenuItems[lobbyIndex],
            });
          } else {
            items.push({ ...localizedMenuItems[lobbyIndex] });
          }
        }

        return items;
      })()
    : localizedMenuItems;
  const assistantLabelByLanguage = {
    es: "ASISTENTE",
    en: "ASSISTANT",
    ru: "ПОМОЩНИК",
    de: "ASSISTENT",
  };
  const assistantAriaLabelByLanguage = {
    es: "ASISTENTE DE XIMA",
    en: "XIMA ASSISTANT",
    ru: "ПОМОЩНИК XIMA",
    de: "XIMA-ASSISTENT",
  };
  const assistantLabel = assistantLabelByLanguage[language] || assistantLabelByLanguage.en;
  const assistantAriaLabel =
    assistantAriaLabelByLanguage[language] || assistantAriaLabelByLanguage.en;
  const visibleMenuItems = localizedMenuItems.filter((item) => {
    const isAdminOnlyMenuItem = item.key === "superusers";
    return !isAdminOnlyMenuItem || hasAdminMenuAccess;
  });
  const mobileAppDownloadPromptCopy = useMemo(() => {
    const copyByLanguage = {
      es: {
        title: "Descargar app movil",
        message: "Deseas descargar la app de XIMA para dispositivos moviles?",
        accept: "Aceptar",
        reject: "Rechazar",
      },
      en: {
        title: "Download mobile app",
        message: "Do you want to download the XIMA app for mobile devices?",
        accept: "Accept",
        reject: "Reject",
      },
      ru: {
        title:
          "\u0421\u043a\u0430\u0447\u0430\u0442\u044c \u043c\u043e\u0431\u0438\u043b\u044c\u043d\u043e\u0435 \u043f\u0440\u0438\u043b\u043e\u0436\u0435\u043d\u0438\u0435",
        message:
          "\u0425\u043e\u0442\u0438\u0442\u0435 \u0441\u043a\u0430\u0447\u0430\u0442\u044c \u043f\u0440\u0438\u043b\u043e\u0436\u0435\u043d\u0438\u0435 XIMA \u0434\u043b\u044f \u043c\u043e\u0431\u0438\u043b\u044c\u043d\u044b\u0445 \u0443\u0441\u0442\u0440\u043e\u0439\u0441\u0442\u0432?",
        accept: "\u041f\u0440\u0438\u043d\u044f\u0442\u044c",
        reject: "\u041e\u0442\u043a\u043b\u043e\u043d\u0438\u0442\u044c",
      },
      de: {
        title: "Mobile-App herunterladen",
        message:
          "M\u00f6chtest du die XIMA-App f\u00fcr mobile Ger\u00e4te herunterladen?",
        accept: "Akzeptieren",
        reject: "Ablehnen",
      },
    };
    return copyByLanguage[normalizeLanguage(language)] || copyByLanguage.en;
  }, [language]);
  const resolvedSidebarWidth = useMemo(() => {
    const fallback = isLandscapeLayout
      ? isIphoneSamsungLandscape
        ? DEFAULT_MOBILE_LANDSCAPE_SIDEBAR_WIDTH
        : DEFAULT_LANDSCAPE_SIDEBAR_WIDTH
      : DEFAULT_SIDEBAR_WIDTH;
    const numericWidth = Number(sidebarWidth);
    return Number.isFinite(numericWidth) ? numericWidth : fallback;
  }, [isIphoneSamsungLandscape, isLandscapeLayout, sidebarWidth]);
  const sidebarPanelStyle = useMemo(() => {
    if (isSidebarCollapsed) return undefined;
    if (isPhoneLandscapeLayout) {
      return {
        width: "25vw",
        minWidth: "25vw",
        maxWidth: "25vw",
        flexBasis: "25vw",
      };
    }
    return { width: `${Math.round(resolvedSidebarWidth)}px` };
  }, [isPhoneLandscapeLayout, isSidebarCollapsed, resolvedSidebarWidth]);
  const mainPanelStyle = useMemo(
    () =>
      isPhoneLandscapeLayout
        ? {
            width: "75vw",
            minWidth: "75vw",
            maxWidth: "75vw",
            flex: "0 0 75vw",
          }
        : undefined,
    [isPhoneLandscapeLayout]
  );
  const sidebarHomeLogoSrc = isSidebarCollapsed
    ? "/logoXparaColapsoPanelIzquierdo.png"
    : "/mejor-logo-336.png";
  const mobileBottomNavButtonSizeClass = isSamsungPortraitMobile
    ? "min-h-[221px] min-w-[297px]"
    : isIphonePortraitMobile
      ? "min-h-[30px] min-w-[38px]"
      : "min-h-[56px] min-w-[74px]";
  const mobileBottomNavIconContainerClass = isSamsungPortraitMobile
    ? "h-22 w-22"
    : isIphonePortraitMobile
      ? "h-[1.1rem] w-[1.1rem]"
      : "h-[2.2rem] w-[2.2rem]";
  const mobileBottomNavIconClass = isSamsungPortraitMobile
    ? "text-[62px]"
    : isIphonePortraitMobile
      ? "text-[11px]"
      : "text-[22px]";
  const mobileBottomNavLabelClass = isSamsungPortraitMobile
    ? "mt-[0.41rem] max-w-[282px] truncate text-[20px] font-bold uppercase tracking-wide"
    : isIphonePortraitMobile
      ? "mt-[0.14rem] max-w-[36px] truncate text-[4.4px] font-bold uppercase tracking-wide"
      : "mt-[0.14rem] max-w-[70px] truncate text-[11px] font-bold uppercase tracking-wide";
  const mobileBottomNavLogoClass = isSamsungPortraitMobile
    ? "h-[12.8rem] max-h-full w-auto object-contain"
    : isIphonePortraitMobile
      ? "h-[2.2rem] max-h-full w-auto object-contain"
      : "h-[4.4rem] max-h-full w-auto object-contain";
  const mobileBottomNavContainerPaddingClass = isSamsungPortraitMobile
    ? "[@media(max-width:1024px)_and_(orientation:portrait)]:p-[0.55rem]"
    : isIphonePortraitMobile
      ? "[@media(max-width:1024px)_and_(orientation:portrait)]:p-[0.14rem]"
      : "[@media(max-width:1024px)_and_(orientation:portrait)]:p-[0.275rem]";
  const mobileBottomNavRailPaddingClass = isSamsungPortraitMobile
    ? "[@media(max-width:1024px)_and_(orientation:portrait)]:pb-[calc(env(safe-area-inset-bottom)+7px)]"
    : isIphonePortraitMobile
      ? "[@media(max-width:1024px)_and_(orientation:portrait)]:pb-[env(safe-area-inset-bottom)]"
      : "[@media(max-width:1024px)_and_(orientation:portrait)]:pb-[calc(env(safe-area-inset-bottom)+2.2px)]";

  const activeMenuKey = useMemo(() => {
    if (location.pathname === "/game/lobby") {
      return "lobby";
    }

    if (location.pathname === "/game/championship") {
      return "championship";
    }

    if (location.pathname === "/game/learn" || location.pathname.startsWith("/game/learn/")) {
      return "learn";
    }

    if (location.pathname === "/game/spectate") {
      return "watch";
    }

    if (location.pathname === "/game/donate" || location.pathname === "/game/support") {
      return "donate";
    }

    if (location.pathname === "/game/store") {
      return "store";
    }

    if (location.pathname === "/game/friends") {
      return "friends";
    }

    if (location.pathname.startsWith("/game/player/")) {
      return "friends";
    }

    if (location.pathname === "/game/history") {
      return "history";
    }

    if (location.pathname === "/game/stats") {
      return "stats";
    }

    if (location.pathname === "/game") {
      const tab = String(new URLSearchParams(location.search).get("tab") || "")
        .trim()
        .toLowerCase();
      if (tab === "admin" || tab === "superusers") {
        return hasAdminMenuAccess ? "superusers" : "home";
      }
      if (tab === "news") return "news";
      if (tab === "history") return "history";
      return "home";
    }

    if (location.pathname.startsWith("/game/settings")) {
      const section = new URLSearchParams(location.search).get("section");
      if (section === "support") return "donate";
      return "settings";
    }

    return null;
  }, [hasAdminMenuAccess, location.pathname, location.search]);

  const routeScrollKey = `${location.pathname}${location.search}${location.hash || ""}`;

  const clearPendingAutoScroll = useCallback(() => {
    if (typeof window === "undefined") return;
    if (autoScrollTimeoutRef.current !== null) {
      window.clearTimeout(autoScrollTimeoutRef.current);
      autoScrollTimeoutRef.current = null;
    }
    if (
      autoScrollRafRef.current !== null &&
      typeof window.cancelAnimationFrame === "function"
    ) {
      window.cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  }, []);

  const hasBlockingModalOpen = useCallback(() => {
    if (typeof document === "undefined" || typeof window === "undefined") return false;
    if (document.body?.classList.contains("ant-scrolling-effect")) return true;

    const modalSelectors = [
      ".ant-modal-root .ant-modal-wrap",
      ".ant-drawer-mask",
      ".xima-replay-modal .ant-modal-wrap",
      ".xima-replay-modal-mobile .ant-modal-wrap",
    ];

    const modalNodes = document.querySelectorAll(modalSelectors.join(","));
    for (const node of modalNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.getAttribute("aria-hidden") === "true") continue;
      const style = window.getComputedStyle(node);
      if (style.display === "none" || style.visibility === "hidden") continue;
      if (node.clientWidth <= 0 && node.clientHeight <= 0) continue;
      return true;
    }
    return false;
  }, []);

  const hasUnityCriticalAnimation = useCallback(() => {
    if (typeof document === "undefined") return false;
    return Boolean(document.querySelector(".match-intro-vs-grid"));
  }, []);

  const scrollSidebarTargetViewsToTop = useCallback(() => {
    if (!isPortraitPhoneViewport()) return false;
    if (typeof window === "undefined" || typeof document === "undefined") return false;

    const scrollTargets = new Set();
    const rootScrollingElement = document.scrollingElement;
    if (rootScrollingElement instanceof HTMLElement) {
      scrollTargets.add(rootScrollingElement);
    }

    document
      .querySelectorAll(".xima-mobile-scroll-page, .xima-mobile-scroll-main")
      .forEach((node) => {
        if (node instanceof HTMLElement) {
          scrollTargets.add(node);
        }
      });

    programmaticScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_GUARD_MS;
    scrollTargets.forEach((node) => {
      if (typeof node.scrollTo === "function") {
        try {
          node.scrollTo({ top: 0, behavior: "smooth" });
          return;
        } catch {
          // Some embedded contexts may reject scroll options; fallback below.
        }
      }
      node.scrollTop = 0;
    });

    if (typeof window.scrollTo === "function") {
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
        window.scrollTo(0, 0);
      }
    }
    return true;
  }, []);

  const queueRouteAutoScrollReset = useCallback(
    (routeKey) => {
      if (!isPortraitPhoneViewport()) return;
      if (typeof window === "undefined") return;

      clearPendingAutoScroll();
      pendingAutoScrollRouteKeyRef.current = routeKey;
      autoScrollRetryCountRef.current = 0;

      const attemptScrollReset = () => {
        if (pendingAutoScrollRouteKeyRef.current !== routeKey) return;
        if (!isPortraitPhoneViewport()) return;

        const userIsScrolling =
          Date.now() - lastManualScrollAtRef.current < USER_SCROLL_GUARD_MS;
        const shouldDefer =
          layoutNavigationLocked ||
          userIsScrolling ||
          hasBlockingModalOpen() ||
          hasUnityCriticalAnimation();

        if (shouldDefer) {
          if (autoScrollRetryCountRef.current >= ROUTE_SCROLL_MAX_RETRIES) return;
          autoScrollRetryCountRef.current += 1;
          autoScrollTimeoutRef.current = window.setTimeout(
            attemptScrollReset,
            ROUTE_SCROLL_RETRY_DELAY_MS
          );
          return;
        }

        scrollSidebarTargetViewsToTop();
        lastAutoScrolledRouteKeyRef.current = routeKey;
        pendingAutoScrollRouteKeyRef.current = "";
      };

      const queueAfterRender = () => {
        autoScrollTimeoutRef.current = window.setTimeout(
          attemptScrollReset,
          ROUTE_SCROLL_POST_RENDER_DELAY_MS
        );
      };

      if (typeof window.requestAnimationFrame === "function") {
        autoScrollRafRef.current = window.requestAnimationFrame(() => {
          autoScrollRafRef.current = window.requestAnimationFrame(queueAfterRender);
        });
      } else {
        queueAfterRender();
      }
    },
    [
      clearPendingAutoScroll,
      hasBlockingModalOpen,
      hasUnityCriticalAnimation,
      layoutNavigationLocked,
      scrollSidebarTargetViewsToTop,
    ]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const listenerOptions = { passive: true, capture: true };
    const markManualScroll = (event) => {
      if (event && event.type === "scroll" && Date.now() <= programmaticScrollUntilRef.current) {
        return;
      }
      lastManualScrollAtRef.current = Date.now();
    };

    window.addEventListener("scroll", markManualScroll, listenerOptions);
    window.addEventListener("touchmove", markManualScroll, listenerOptions);
    window.addEventListener("wheel", markManualScroll, listenerOptions);

    return () => {
      window.removeEventListener("scroll", markManualScroll, listenerOptions);
      window.removeEventListener("touchmove", markManualScroll, listenerOptions);
      window.removeEventListener("wheel", markManualScroll, listenerOptions);
    };
  }, []);

  useEffect(() => {
    if (!routeScrollKey) return;
    if (
      lastAutoScrolledRouteKeyRef.current === routeScrollKey &&
      !layoutNavigationLocked
    ) {
      return;
    }
    queueRouteAutoScrollReset(routeScrollKey);
  }, [layoutNavigationLocked, queueRouteAutoScrollReset, routeScrollKey]);

  useEffect(() => () => clearPendingAutoScroll(), [clearPendingAutoScroll]);

  const downloadXimaMobileApp = useCallback(() => {
    if (typeof document === "undefined") return;
    const link = document.createElement("a");
    link.href = XIMA_MOBILE_APP_DOWNLOAD_URL;
    link.download = XIMA_MOBILE_APP_FILE_NAME;
    document.body.appendChild(link);
    link.click();
    link.remove();
    writeStoredString(mobileAppDownloadedStorageKey, "1");
    setHasDownloadedXimaMobileApp(true);
  }, [mobileAppDownloadedStorageKey]);

  const requestXimaMobileAppDownload = useCallback(() => {
    setIsMobileAppDownloadPromptOpen(true);
  }, []);

  const confirmXimaMobileAppDownload = useCallback(() => {
    setIsMobileAppDownloadPromptOpen(false);
    downloadXimaMobileApp();
  }, [downloadXimaMobileApp]);

  const rejectXimaMobileAppDownload = useCallback(() => {
    setIsMobileAppDownloadPromptOpen(false);
  }, []);

  const handleLogout = async () => {
    const shouldLogout =
      typeof window === "undefined" ? true : window.confirm(t("CONFIRMAR_CERRAR_SESION"));
    if (!shouldLogout) return;

    try {
      if (typeof window !== "undefined") {
        sessionStorage.removeItem(GLOBAL_CHAT_SESSION_KEY);
      }
    } catch (error) {
      console.log(error);
    }
    await logout();
  };

  const handleMenuClick = (route, menuKey = "") => {
    markSidebarMenuBadgeAsSeen(menuKey);
    if (menuKey === "home") {
      if (isPortraitPhoneViewport()) {
        navigate("/game/lobby", { replace: true });
        return;
      }
      const homeRoute = route === "/game" ? "/game?from=home-button" : route;
      navigate(homeRoute);
      return;
    }
    navigate(route);
  };

  useEffect(() => {
    if (activeMenuKey !== "friends") return;
    if (!socialAlert.newestPendingAt) return;
    setSeenSocialAlertTimestamp(socialAlert.newestPendingAt);
    setSocialAlert((prev) => ({
      ...prev,
      unseen: false,
    }));
  }, [activeMenuKey, socialAlert.newestPendingAt]);

  useEffect(() => {
    if (!activeMenuKey) return;
    markSidebarMenuBadgeAsSeen(activeMenuKey);
  }, [activeMenuKey, markSidebarMenuBadgeAsSeen]);

  useEffect(() => {
    const resolveTutorialRoute = (tutorialId) => {
      const normalized = String(tutorialId || "").trim().toLowerCase();
      if (!normalized) return "/game/learn";

      if (["match_start", "match-start", "partida_inicial", "quickstart"].includes(normalized)) {
        return "/game/learn";
      }

      if (normalized === "settings") {
        return "/game/settings?section=general";
      }

      if (normalized === "support" || normalized === "soporte") {
        return "/game/settings?section=support";
      }

      return "/game/learn";
    };

    const handleTutorial = (event) => {
      const tutorialId = String(event?.detail?.tutorial || event?.detail?.id || "").trim();
      if (!tutorialId) return;

      const target = resolveTutorialRoute(tutorialId);
      navigate(target, { replace: false });
    };

    const eventName = "xima-chat-tutorial";
    window.addEventListener(eventName, handleTutorial);
    return () => window.removeEventListener(eventName, handleTutorial);
  }, [navigate]);

  useEffect(() => {
    const isTournamentRoute = /^\/game\/[^/]+$/.test(location.pathname);
    if (!isTournamentRoute && layoutNavigationLocked) {
      setLayoutNavigationLocked(false);
    }
  }, [layoutNavigationLocked, location.pathname]);

  return (
    <div
      className={`xima-game-shell xima-mobile-scroll-page flex h-screen overflow-hidden bg-center bg-cover [@media(max-width:1024px)_and_(orientation:portrait)]:flex-col ${
        useDesktopLayoutForPhoneLandscape ? "xima-phone-landscape-desktop" : ""
      }`}
      style={{
        backgroundImage: "url(/background.jpg)",
        ...(isSamsungPortraitMobile ? { height: "100dvh" } : {}),
      }}
    >
      {shouldBlockPhoneLandscape ? (
        <div className="fixed inset-0 z-[160] flex items-center justify-center bg-[#030915f2] px-5 text-center">
          <div className="max-w-sm rounded-xl border border-[#22ff55]/70 bg-[#10151de8] px-5 py-4">
            <div className="text-sm font-black uppercase tracking-wide text-[#22ff55]">
              {phoneLandscapeBlockCopy.title}
            </div>
            <div className="mt-2 text-sm text-[#dbe8ff]">
              {phoneLandscapeBlockCopy.line1}
            </div>
            <div className="mt-2 text-sm text-[#dbe8ff]">
              {phoneLandscapeBlockCopy.line2}
            </div>
          </div>
        </div>
      ) : null}
      <aside
        data-xima-chat-sidebar="left"
        className={`xima-game-sidebar z-10 flex shrink-0 flex-col border-r border-[#22ff55]/60 bg-[#11161fcc] p-2 backdrop-blur-sm ${
          isSidebarCollapsed
            ? "w-[92px]"
            : "md:p-3 [@media(max-width:1024px)_and_(orientation:landscape)]:p-2"
        } [@media(max-width:1024px)_and_(orientation:landscape)]:overflow-y-auto [@media(max-width:1024px)_and_(orientation:landscape)]:pb-[calc(env(safe-area-inset-bottom)+120px)] [@media(max-width:1024px)_and_(orientation:portrait)]:fixed [@media(max-width:1024px)_and_(orientation:portrait)]:bottom-0 [@media(max-width:1024px)_and_(orientation:portrait)]:left-0 [@media(max-width:1024px)_and_(orientation:portrait)]:right-0 [@media(max-width:1024px)_and_(orientation:portrait)]:z-40 [@media(max-width:1024px)_and_(orientation:portrait)]:w-full [@media(max-width:1024px)_and_(orientation:portrait)]:border-r-0 [@media(max-width:1024px)_and_(orientation:portrait)]:border-t ${mobileBottomNavContainerPaddingClass} [@media(max-width:1024px)_and_(orientation:portrait)]:overflow-x-auto [@media(max-width:1024px)_and_(orientation:portrait)]:overflow-y-hidden ${
          layoutNavigationLocked
            ? "xima-game-navigation-locked pointer-events-none select-none opacity-70"
            : ""
        }`}
        style={sidebarPanelStyle}
        onClickCapture={handleSidebarButtonSoundCapture}
      >
        {isMobileSidebar ? (
          <div
            className={`scrollbar-gamified flex w-max min-w-full items-stretch gap-2 pr-1 [@media(max-width:1024px)_and_(orientation:portrait)]:gap-[0.14rem] [@media(max-width:1024px)_and_(orientation:portrait)]:pr-0 ${mobileBottomNavRailPaddingClass}`}
          >
            <button
              type="button"
              aria-label={`${t("IDIOMA")}: ${language.toUpperCase()}`}
              data-sidebar-tooltip-trigger="true"
              data-xima-menu-key="language"
              onClick={(event) =>
                runSidebarActionWithMobileTooltip(
                  event,
                  "language",
                  `${t("IDIOMA")}: ${language.toUpperCase()}`,
                  () => navigate("/game/settings?section=language")
                )
              }
              className={`flex ${mobileBottomNavButtonSizeClass} flex-col items-center justify-center rounded-xl border border-[#2f3f5c] bg-[#0f1728] px-1 text-[#92b6ff] transition hover:border-[#4fc3ff] hover:text-[#d7ebff]`}
            >
              <IoLanguageOutline className={mobileBottomNavIconClass} aria-hidden="true" />
              <span className={mobileBottomNavLabelClass}>
                {language.toUpperCase()}
              </span>
            </button>

            {ENABLE_XIMA_CHATBOT ? (
              <button
                type="button"
                aria-label={assistantAriaLabel}
                data-sidebar-tooltip-trigger="true"
                data-xima-menu-key="assistant"
                data-chatbot-launcher="true"
                onClick={(event) =>
                  runSidebarActionWithMobileTooltip(
                    event,
                    "assistant",
                    assistantAriaLabel,
                    openXimaAssistant
                  )
                }
                className={`flex ${mobileBottomNavButtonSizeClass} flex-col items-center justify-center rounded-xl border border-[#2f3f5c] bg-[#0f1728] px-1 text-[#92b6ff] transition hover:border-[#4fc3ff] hover:text-[#d7ebff]`}
              >
                <IoGameControllerOutline className={mobileBottomNavIconClass} aria-hidden="true" />
                <span className={mobileBottomNavLabelClass}>{assistantLabel}</span>
              </button>
            ) : null}

            <button
              type="button"
              aria-label={mobileAppDownloadPromptCopy.title}
              data-sidebar-tooltip-trigger="true"
              data-xima-chat-anchor="home-menu"
              data-xima-menu-key="home"
              onClick={(event) =>
                runSidebarActionWithMobileTooltip(
                  event,
                  "home-logo",
                  mobileAppDownloadPromptCopy.title,
                  requestXimaMobileAppDownload
                )
              }
              className={`relative flex ${mobileBottomNavButtonSizeClass} flex-col items-center justify-center rounded-xl border border-[#22ff55]/50 bg-[#0d1117] px-1 text-[#f2ffef] transition hover:bg-[#0a0e13]`}
            >
              {!hasDownloadedXimaMobileApp ? (
                <span
                  className="absolute -right-1.5 -top-1.5 h-4 w-4 rounded-full border border-[#ffb3b3] bg-[#ff0000] shadow-[0_0_8px_rgba(255,0,0,0.85)]"
                  aria-hidden="true"
                />
              ) : null}
              <img src={sidebarHomeLogoSrc} alt="Xima" className={mobileBottomNavLogoClass} />
            </button>

            {visibleMenuItems.map((item) => {
              const isActive = item.key === activeMenuKey;
              const hasPendingSocial = item.key === "friends" && socialAlert.pendingTotal > 0;
              const highlightFriends = hasPendingSocial && socialAlert.unseen && !isActive;
              const menuBadgeCount = getSidebarMenuBadgeCount(item.key);
              const hasSidebarBadge = menuBadgeCount > 0;
              const MenuIcon = MENU_ICON_BY_KEY[item.key] || IoGameControllerOutline;
              return (
                <button
                  key={item.key}
                  type="button"
                  aria-label={item.label}
                  data-sidebar-tooltip-trigger="true"
                  data-xima-menu-key={item.key}
                  data-xima-active={isActive ? "true" : undefined}
                  onClick={(event) =>
                    runSidebarActionWithMobileTooltip(event, item.key, item.label, () =>
                      handleMenuClick(item.route, item.key)
                    )
                  }
                  className={`relative flex ${mobileBottomNavButtonSizeClass} flex-col items-center justify-center rounded-xl border px-1 transition ${
                    isActive
                      ? "border-[#51ff74] bg-[#183b2a] text-[#cbffdc]"
                      : highlightFriends
                      ? "border-[#ffb04d] bg-[#3b2410] text-[#ffd7a1] hover:border-[#ffd17d] hover:text-[#fff0d1]"
                      : "border-[#232a36] bg-[#10151d] text-[#e9eef6] hover:border-[#22ff55] hover:text-[#22ff55]"
                  }`}
                >
                  <span
                    className={`relative inline-flex ${mobileBottomNavIconContainerClass} items-center justify-center rounded-lg border border-[#4b566d] bg-[#0b1018]`}
                  >
                    <MenuIcon className={mobileBottomNavIconClass} aria-hidden="true" />
                    {hasSidebarBadge ? (
                      <span className="absolute -right-2 -top-2 inline-flex min-w-[16px] items-center justify-center rounded-full border border-[#ffb3b3] bg-[#ff0000] px-1 py-[1px] text-[8px] font-bold leading-none text-white">
                        {menuBadgeCount}
                      </span>
                    ) : null}
                  </span>
                  <span className={mobileBottomNavLabelClass}>
                    {item.label}
                  </span>
                </button>
              );
            })}

            <button
              type="button"
              aria-label={t("CERRAR_SESION")}
              data-sidebar-tooltip-trigger="true"
              data-xima-menu-key="logout"
              onClick={(event) =>
                runSidebarActionWithMobileTooltip(
                  event,
                  "logout-mobile",
                  t("CERRAR_SESION"),
                  handleLogout
                )
              }
              className={`flex ${mobileBottomNavButtonSizeClass} flex-col items-center justify-center rounded-xl border border-[#ff8a24] bg-[#1b130c] px-1 text-[#ffb974] transition hover:bg-[#29190d]`}
            >
              <IoLogOutOutline className={mobileBottomNavIconClass} aria-hidden="true" />
              <span className={mobileBottomNavLabelClass}>{t("CERRAR_SESION")}</span>
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              aria-label={`${t("IDIOMA")}: ${language.toUpperCase()}`}
              data-sidebar-tooltip-trigger="true"
              data-xima-menu-key="language"
              onClick={(event) =>
                runSidebarActionWithMobileTooltip(
                  event,
                  "language",
                  `${t("IDIOMA")}: ${language.toUpperCase()}`,
                  () => navigate("/game/settings?section=language")
                )
              }
              className={`mb-3 w-full rounded-xl border border-[#2f3f5c] bg-[#0f1728] text-[#92b6ff] transition hover:border-[#4fc3ff] hover:text-[#d7ebff] ${
                isSidebarCollapsed
                  ? "flex min-h-[44px] items-center justify-center px-0 py-2 text-2xl"
                  : "px-2 py-1.5 text-center text-xs font-bold uppercase tracking-[0.14em] md:text-sm [@media(max-width:1024px)_and_(orientation:landscape)]:py-1 [@media(max-width:1024px)_and_(orientation:landscape)]:text-[10px]"
              }`}
            >
              {isSidebarCollapsed ? (
                <IoLanguageOutline aria-hidden="true" />
              ) : (
                <>
                  {t("IDIOMA")}: {language.toUpperCase()}
                </>
              )}
            </button>

            {ENABLE_XIMA_CHATBOT ? (
              <button
                type="button"
                aria-label={assistantAriaLabel}
                data-sidebar-tooltip-trigger="true"
                data-xima-menu-key="assistant"
                onClick={(event) =>
                  runSidebarActionWithMobileTooltip(
                    event,
                    "assistant-desktop",
                    assistantAriaLabel,
                    openXimaAssistant
                  )
                }
                className={`mb-2 w-full rounded-xl border border-[#2f3f5c] bg-[#0f1728] text-[#92b6ff] transition hover:border-[#4fc3ff] hover:text-[#d7ebff] ${
                  isSidebarCollapsed
                    ? "flex min-h-[44px] items-center justify-center px-0 py-2 text-2xl"
                    : "px-2 py-1.5 text-center text-xs font-bold uppercase tracking-[0.14em] md:text-sm [@media(max-width:1024px)_and_(orientation:landscape)]:py-1 [@media(max-width:1024px)_and_(orientation:landscape)]:text-[10px]"
                }`}
              >
                {isSidebarCollapsed ? (
                  <IoGameControllerOutline aria-hidden="true" />
                ) : (
                  assistantAriaLabel
                )}
              </button>
            ) : null}

            <button
              type="button"
              aria-label={mobileAppDownloadPromptCopy.title}
              data-sidebar-tooltip-trigger="true"
              data-xima-chat-anchor="home-menu"
              data-xima-menu-key="home"
              onClick={(event) =>
                runSidebarActionWithMobileTooltip(
                  event,
                  "home-logo",
                  mobileAppDownloadPromptCopy.title,
                  requestXimaMobileAppDownload
                )
              }
              className={`relative mb-2 flex w-full items-center justify-center rounded-xl border border-[#22ff55]/50 bg-[#0d1117] hover:bg-[#0a0e13] ${
                isSidebarCollapsed
                  ? "h-14 px-1"
                  : "h-20 px-2 md:h-24 [@media(max-width:1024px)_and_(orientation:landscape)]:h-14"
              }`}
            >
              {!hasDownloadedXimaMobileApp ? (
                <span
                  className="absolute -right-1.5 -top-1.5 h-4 w-4 rounded-full border border-[#ffb3b3] bg-[#ff0000] shadow-[0_0_8px_rgba(255,0,0,0.85)]"
                  aria-hidden="true"
                />
              ) : null}
              <img
                src={sidebarHomeLogoSrc}
                alt="Xima"
                className={`w-auto object-contain ${
                  isSidebarCollapsed
                    ? "h-full max-h-[52px]"
                    : "h-full max-h-[74px] scale-x-[1.25] md:max-h-[92px] md:scale-x-[1.25]"
                }`}
              />
            </button>

            {!isPhoneLandscapeLayout ? (
              <button
                type="button"
                aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                onClick={() => setIsDesktopSidebarCollapsed((previous) => !previous)}
                className="mb-3 flex min-h-[44px] w-full items-center justify-center rounded-xl border border-[#2f3f5c] bg-[#0f1728] px-1.5 py-2 text-[#92b6ff] transition hover:border-[#4fc3ff] hover:text-[#d7ebff] [@media(max-width:1024px)_and_(orientation:landscape)]:min-h-[36px] [@media(max-width:1024px)_and_(orientation:landscape)]:py-1"
              >
                {isSidebarCollapsed ? (
                  <IoChevronForwardOutline className="text-2xl" aria-hidden="true" />
                ) : (
                  <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] md:text-sm">
                    <IoChevronBackOutline className="text-base" aria-hidden="true" />
                    Collapse
                  </span>
                )}
              </button>
            ) : null}

            <div
              className={`scrollbar-gamified flex-1 overflow-y-auto ${
                isSidebarCollapsed ? "pr-0" : "pr-1"
              } [@media(max-width:1024px)_and_(orientation:landscape)]:flex-none [@media(max-width:1024px)_and_(orientation:landscape)]:overflow-visible [@media(max-width:1024px)_and_(orientation:landscape)]:pr-0`}
            >
              <div className="space-y-1">
                {visibleMenuItems.map((item) => {
                  const isActive = item.key === activeMenuKey;
                  const hasPendingSocial = item.key === "friends" && socialAlert.pendingTotal > 0;
                  const highlightFriends = hasPendingSocial && socialAlert.unseen && !isActive;
                  const menuBadgeCount = getSidebarMenuBadgeCount(item.key);
                  const hasSidebarBadge = menuBadgeCount > 0;
                  const MenuIcon = MENU_ICON_BY_KEY[item.key] || IoGameControllerOutline;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      aria-label={item.label}
                      data-sidebar-tooltip-trigger="true"
                      data-xima-menu-key={item.key}
                      data-xima-active={isActive ? "true" : undefined}
                      onClick={(event) =>
                        runSidebarActionWithMobileTooltip(event, item.key, item.label, () =>
                          handleMenuClick(item.route, item.key)
                        )
                      }
                      className={`relative w-full rounded-xl border font-semibold transition ${
                        isSidebarCollapsed
                          ? "flex min-h-[44px] items-center justify-center px-0 py-2"
                          : "px-2 py-2.5 text-left text-xs uppercase tracking-wide md:px-3 md:text-xl md:leading-7 [@media(max-width:1024px)_and_(orientation:landscape)]:py-1.5 [@media(max-width:1024px)_and_(orientation:landscape)]:text-[10px] [@media(max-width:1024px)_and_(orientation:landscape)]:leading-4 [@media(max-width:1024px)_and_(orientation:landscape)]:tracking-[0.02em]"
                      } ${
                        isActive
                          ? "border-[#51ff74] bg-[#183b2a] text-[#cbffdc]"
                          : highlightFriends
                          ? "border-[#ffb04d] bg-[#3b2410] text-[#ffd7a1] hover:border-[#ffd17d] hover:text-[#fff0d1]"
                          : "border-[#232a36] bg-[#10151d] text-[#e9eef6] hover:border-[#22ff55] hover:text-[#22ff55]"
                      }`}
                    >
                      {isSidebarCollapsed ? (
                        <span className="relative inline-flex items-center justify-center">
                          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[#4b566d] bg-[#0b1018]">
                            <MenuIcon className="text-2xl" aria-hidden="true" />
                          </span>
                          {hasSidebarBadge ? (
                            <span className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full border border-[#ffb3b3] bg-[#ff0000] px-1 py-[1px] text-[9px] font-bold leading-none text-white">
                              {menuBadgeCount}
                            </span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-1.5 md:gap-2">
                            <span className="truncate">{item.label}</span>
                            {hasSidebarBadge ? (
                              <span className="inline-flex items-center justify-center rounded-full border border-[#ffb3b3] bg-[#ff0000] px-1.5 py-[1px] text-[8px] font-bold leading-none text-white md:text-[10px]">
                                {menuBadgeCount}
                              </span>
                            ) : null}
                          </span>
                          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[#4b566d] bg-[#0b1018] md:h-[72px] md:w-[72px] [@media(max-width:1024px)_and_(orientation:landscape)]:h-8 [@media(max-width:1024px)_and_(orientation:landscape)]:w-8">
                            <MenuIcon
                              className="text-2xl md:text-5xl [@media(max-width:1024px)_and_(orientation:landscape)]:text-lg"
                              aria-hidden="true"
                            />
                          </span>
                        </span>
                      )}
                    </button>
                  );
                })}

                <button
                  type="button"
                  aria-label={t("CERRAR_SESION")}
                  data-sidebar-tooltip-trigger="true"
                  data-xima-menu-key="logout"
                  onClick={(event) =>
                    runSidebarActionWithMobileTooltip(
                      event,
                      "logout",
                      t("CERRAR_SESION"),
                      handleLogout
                    )
                  }
                  className={`w-full rounded-xl border border-[#ff8a24] bg-[#1b130c] text-[#ffb974] transition hover:bg-[#29190d] ${
                    isSidebarCollapsed
                      ? "flex min-h-[44px] items-center justify-center px-0 py-2 text-2xl"
                      : "px-2 py-2 text-sm font-bold uppercase tracking-wide md:text-base [@media(max-width:1024px)_and_(orientation:landscape)]:mb-8"
                  }`}
                >
                  {isSidebarCollapsed ? (
                    <IoLogOutOutline aria-hidden="true" />
                  ) : (
                    t("CERRAR_SESION")
                  )}
                </button>
                <div
                  id="xima-direct-message-desktop-anchor"
                  className="xima-direct-message-desktop-anchor"
                />
              </div>
            </div>

            <div
              className={`mt-3 flex items-center justify-center rounded-md border border-[#2f3f5c] bg-[#0f1728] ${
                isSidebarCollapsed ? "px-1 py-1.5" : "px-2 py-2"
              }`}
            >
              <img
                src="/logomuino.png"
                alt="Muino"
                className={isSidebarCollapsed ? "h-4 w-auto" : "h-[18px] w-auto md:h-6"}
              />
            </div>

            {layoutNavigationLocked && !isSidebarCollapsed ? (
              <div className="mt-2 rounded-lg border border-yellow-500/50 bg-yellow-900/30 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-yellow-200 md:text-xs">{i18nT("common.menuBloqueadoDurantePartida")}</div>
            ) : null}
          </>
        )}
      </aside>
      {isMobileAppDownloadPromptOpen ? (
        <div className="fixed inset-0 z-[180] flex items-center justify-center bg-[#020711cc] px-4">
          <div
            className="w-full max-w-sm rounded-lg border border-[#22ff55]/70 bg-[#10151df5] p-5 text-center shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="xima-mobile-app-download-title"
          >
            <h2
              id="xima-mobile-app-download-title"
              className="text-base font-black uppercase tracking-wide text-[#22ff55]"
            >
              {mobileAppDownloadPromptCopy.title}
            </h2>
            <p className="mt-3 text-sm font-semibold leading-6 text-[#dbe8ff]">
              {mobileAppDownloadPromptCopy.message}
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={rejectXimaMobileAppDownload}
                className="rounded-lg border border-[#6f7b92] bg-[#182131] px-3 py-2 text-sm font-bold uppercase tracking-wide text-[#d7e3f7] transition hover:border-[#a7b6d3] hover:bg-[#202c40]"
              >
                {mobileAppDownloadPromptCopy.reject}
              </button>
              <button
                type="button"
                onClick={confirmXimaMobileAppDownload}
                className="rounded-lg border border-[#22ff55] bg-[#12351f] px-3 py-2 text-sm font-bold uppercase tracking-wide text-[#d8ffe1] transition hover:bg-[#174629]"
              >
                {mobileAppDownloadPromptCopy.accept}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {!isSidebarCollapsed && !isMobileSidebar && !isPhoneLandscapeLayout ? (
        <div
          role="separator"
          aria-label={i18nT("common.resizeLeftPanel")}
          aria-orientation="vertical"
          onPointerDown={handleSidebarResizeStart}
          className="xima-game-sidebar-resizer z-20 w-[5px] shrink-0 cursor-col-resize bg-[#2fc97066] transition hover:bg-[#32f58acc] active:bg-[#43ff9f]"
        />
      ) : null}
      {isMobileSidebar && isSidebarCollapsed && mobileTooltip.key ? (
        <div
          className="pointer-events-none fixed z-[90] max-w-[136px] -translate-x-1/2 -translate-y-full rounded-md border border-[#6f87ad] bg-[#0d1625f2] px-2 py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-[#ecf4ff] shadow-2xl"
          style={{ left: mobileTooltip.x, top: mobileTooltip.y }}
          role="status"
          aria-live="polite"
        >
          {mobileTooltip.label}
        </div>
      ) : null}

      <main
        className="xima-game-main xima-mobile-scroll-main relative min-w-0 min-h-0 flex-1 border border-[#22ff55]/70 bg-[#0d1117b3] shadow-[inset_0_0_45px_rgba(20,25,35,0.85)] [@media(max-width:1024px)_and_(orientation:portrait)]:order-1"
        style={mainPanelStyle}
      >
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_75%_20%,rgba(34,255,85,0.12),transparent_40%),radial-gradient(circle_at_10%_80%,rgba(255,138,36,0.08),transparent_40%)]" />
        <div className="relative z-10 h-full min-h-0 [@media(max-width:1024px)]:h-auto">
          {showLobbySourcePanel ? (
            <div className="mb-3 rounded-xl border border-[#24406f] bg-[#070f24e6] p-3 shadow-[0_0_30px_rgba(0,0,0,0.35)]">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#1f6fa8]/70 bg-[#06172c]/90 px-4 py-3 shadow-[0_12px_34px_rgba(0,0,0,0.35)]">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.28em] text-[#9de8ff]">{i18nT("common.loby")}</div>
                  <div className="text-2xl font-black uppercase tracking-[0.14em] text-white">
                    {lobbyModePanelTitle}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/game/lobby", { replace: true })}
                  className="rounded-xl border border-[#44ffaf]/70 bg-[#123927] px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-[#d9ffe9] transition hover:border-[#7cffc0] hover:bg-[#1d5b3e]"
                >{i18nT("common.volverAlLoby")}</button>
              </div>
            </div>
          ) : null}
          <Outlet
            context={{
              language,
              setLanguage,
              socialAlert,
              refreshSocialAlert: fetchSocialAlert,
              isSamsungPortraitMobile,
              isIphonePortraitMobile,
              hasAdminMenuAccess,
              setLayoutNavigationLocked,
            }}
          />
        </div>
      </main>
      <DirectMessages language={language} />
    </div>
  );
};

export default GameLayout;
